import React from 'react';
import PropTypes from 'prop-types';
import {
  IconLogo,
  Link,
} from '@resy/ui-kitchen';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import { handleMktAnalytics } from '../../AmexMarketingUtils/AmexUtils';
import { ACQUISITION_CTA_EVENT_NAME } from '../../AmexMarketingUtils/constants';
import './AmexExperientialNAA.scss';

const AmexExperientialNAA = (
  {
    baseRequestUrl,
    naaConfig: {
      section_id: sectionId,
      active_theme: activeTheme,
      themes = [],
    },
    Analytics,
    matchMedia,
  }
) => {
  if (themes.length === 0) return false;
  const currentTheme = themes.find((theme) => theme.name === activeTheme);

  const {
    name: themeName = '',
    title,
    copy_title: copyTitle = '',
    subtitle,
    subtitle_2: subtitle2 = '',
    text,
    badge = '',
    fine_print: finePrint = '',
    card_image: {
      desktop: desktopImage = '',
      mobile: mobileImage = '',
    } = {},
    logo: sectionLogo,
    logo_resy: logoResy = '',
    cta: {
      label = '',
      url = '',
      utm = '',
    } = {},
  } = currentTheme;

  const sanitizedTitle = sanitizeString(title);
  const sanitizedSubtitle = sanitizeString(subtitle);
  const sanitizedSubtitle2 = sanitizeString(subtitle2);
  const sanitizedFinePrint = sanitizeString(finePrint);
  const sanitizedText = sanitizeString(text);
  const sanitizedCopyTitle = sanitizeString(copyTitle);
  const sanitizedBadge = sanitizeString(badge);

  const cardImage = matchMedia.md ? desktopImage : mobileImage;
  const subtitleLogo = () => (
    <span role="img" aria-label="RESY">
      <IconLogo width="72px" height="31px" />
    </span>
  );

  const displaySubtitle = () => (
    <h4 className="AmexExperientialNAA__subtitle">
      <span
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: sanitizedSubtitle,
        }}
      />
      {subtitle2 && (
        <>
          {subtitleLogo()}
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedSubtitle2,
            }}
          />
        </>
      )}
    </h4>
  );

  const displayTitle = () => (
    <>
      {logoResy && (
        <div className="AmexExperientialNAA--logo--wrapper">
          <img
            className="AmexExperientialNAA--logo--resy"
            src={`${baseRequestUrl}Experiential/images/${logoResy}`}
            alt="Resy Amex logo"
          />
        </div>
      )}
      {copyTitle && (
      <h3
        {...(!title && { id: sectionId })}
        className="AmexExperientialNAA__copyTitle"
      >
        <span
        // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitizedCopyTitle,
          }}
        />
      </h3>
      )}
      {subtitle && displaySubtitle()}
    </>
  );

  return (
    <div className={`AmexExperientialNAA ${themeName}`}>
      <div className="container">
        <div className="AmexExperientialNAA__wrapper">
          <div className="AmexExperientialNAA__wrapper--title">
            {title && (
              <h2
                id={sectionId}
                className="AmexExperientialNAA__title"
              >
                <span
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: sanitizedTitle,
                  }}
                />
              </h2>
            )}
          </div>

          {!matchMedia.lg && displayTitle()}
          <div className="AmexExperientialNAA__content">
            <div className="AmexExperientialNAA__image">
              {cardImage && (
                <img
                  className="logo-img"
                  src={`${baseRequestUrl}Experiential/images/${cardImage}`}
                  alt=""
                />
              )}
              {finePrint && (
                <p
                  className="AmexExperientialNAA__fineprint"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: sanitizedFinePrint,
                  }}
                />
              )}
            </div>
            <div className="AmexExperientialNAA__copy">
              {sectionLogo && (
                <img
                  src={`${baseRequestUrl}Experiential/images/${sectionLogo}`}
                  className="AmexExperientialNAA__logo"
                  alt="Global Dining Access logo"
                />
              )}
              {badge && (
                <p
                  className="AmexExperientialNAA__badge"
                  dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                    __html: sanitizedBadge,
                  }}
                />
              )}
              {matchMedia.lg && displayTitle()}
              {text && (
                <p
                  className="AmexExperientialNAA__text"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: sanitizedText,
                  }}
                />
              )}
              {(url && label) && (
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${url}${utm}`}
                  className="AmexExperientialNAA__cta"
                  onClick={() => {
                    const extraParam = {
                      action_name: 'NAA - Click',
                    };
                    handleMktAnalytics(Analytics.event, ACQUISITION_CTA_EVENT_NAME, extraParam);
                  }}
                >
                  {label}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

AmexExperientialNAA.propTypes = {
  baseRequestUrl: PropTypes.string.isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  naaConfig: PropTypes.shape({
    section_id: PropTypes.string,
    active_theme: PropTypes.string,
    themes: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      title: PropTypes.string,
      subtitle: PropTypes.string,
      subtitle_2: PropTypes.string,
      text: PropTypes.string,
      logo: PropTypes.string,
      fine_print: PropTypes.string,
      card_image: PropTypes.shape({
        desktop: PropTypes.string,
        mobile: PropTypes.string,
      }),
      cta: PropTypes.shape({
        label: PropTypes.string,
        url: PropTypes.string,
        utm: PropTypes.string,
      }),
    })),
  }).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    ipadLandscape: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
};

export default AmexExperientialNAA;
